(() => {
  const charset = " .,:;irsXA253hMHGS#9B&@";
  const maxDesktopColumns = 180;
  const maxMobileColumns = 96;
  const desktopFrameInterval = 1000 / 15;
  const mobileFrameInterval = 1000 / 12;
  const completionThreshold = 0.35;
  const stallThreshold = 8000;
  const cleanups = new WeakMap();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function mount(root) {
    if (!root || cleanups.has(root)) return;

    const video = root.querySelector("[data-frml-ascii-video]");
    const canvas = root.querySelector("[data-frml-ascii-canvas]");
    const resumeButton = root.querySelector("[data-frml-ascii-resume]");
    const discount = root.querySelector("[data-frml-ascii-discount]");
    const context = canvas?.getContext("2d", { alpha: false });
    if (!video || !canvas || !resumeButton || !discount || !context) return;

    const sampleCanvas = document.createElement("canvas");
    const sampleContext = sampleCanvas.getContext("2d", {
      alpha: false,
      willReadFrequently: true,
    });
    if (!sampleContext) return;

    let animationFrame = 0;
    let videoFrame = 0;
    let lastFrameAt = 0;
    let lastMediaTime = 0;
    let lastProgressAt = performance.now();
    let automaticRecoveryAttempts = 0;
    let prematureEndRecoveries = 0;
    let pauseRecoveryTimer = 0;
    let luminance = new Float32Array(0);
    let disposed = false;
    let completed = false;

    const setInteractionRequired = (required) => {
      resumeButton.hidden = !required;
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.25);
      const width = Math.max(1, Math.round(bounds.width * pixelRatio));
      const height = Math.max(1, Math.round(bounds.height * pixelRatio));

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    };

    const drawVideoCover = (targetColumns, targetRows, targetAspect) => {
      const sourceWidth = video.videoWidth;
      const sourceHeight = video.videoHeight;
      const sourceAspect = sourceWidth / sourceHeight;
      let sourceX = 0;
      let sourceY = 0;
      let cropWidth = sourceWidth;
      let cropHeight = sourceHeight;

      if (sourceAspect > targetAspect) {
        cropWidth = sourceHeight * targetAspect;
        sourceX = (sourceWidth - cropWidth) / 2;
      } else {
        cropHeight = sourceWidth / targetAspect;
        sourceY = (sourceHeight - cropHeight) / 2;
      }

      sampleContext.drawImage(
        video,
        sourceX,
        sourceY,
        cropWidth,
        cropHeight,
        0,
        0,
        targetColumns,
        targetRows,
      );
    };

    const render = () => {
      if (!video.videoWidth || !canvas.width || !canvas.height) return;

      const compact = canvas.clientWidth < 640;
      const targetColumns = compact
        ? Math.round(clamp(canvas.clientWidth / 4, 76, maxMobileColumns))
        : Math.round(clamp(canvas.clientWidth / 8, 128, maxDesktopColumns));
      const viewportAspect = canvas.width / canvas.height;
      const targetRows = Math.max(
        1,
        Math.round((targetColumns / viewportAspect) * 0.58),
      );
      const sampleLength = targetColumns * targetRows;

      if (
        sampleCanvas.width !== targetColumns ||
        sampleCanvas.height !== targetRows
      ) {
        sampleCanvas.width = targetColumns;
        sampleCanvas.height = targetRows;
      }
      if (luminance.length !== sampleLength) {
        luminance = new Float32Array(sampleLength);
      }

      drawVideoCover(targetColumns, targetRows, viewportAspect);
      const pixels = sampleContext.getImageData(
        0,
        0,
        targetColumns,
        targetRows,
      ).data;

      for (let index = 0; index < sampleLength; index += 1) {
        const offset = index * 4;
        luminance[index] =
          0.2126 * pixels[offset] +
          0.7152 * pixels[offset + 1] +
          0.0722 * pixels[offset + 2];
      }

      const width = canvas.width;
      const height = canvas.height;
      const cellWidth = width / targetColumns;
      const cellHeight = height / targetRows;

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.fillStyle = "#050609";
      context.fillRect(0, 0, width, height);
      context.fillStyle = "#d6d2c6";
      context.font = `${cellHeight * 1.04}px Consolas, "Courier New", monospace`;
      context.textAlign = "left";
      context.textBaseline = "top";

      const glyphWidth = Math.max(1, context.measureText("M").width);
      context.save();
      context.scale(cellWidth / glyphWidth, 1);

      for (let row = 0; row < targetRows; row += 1) {
        let line = "";
        for (let column = 0; column < targetColumns; column += 1) {
          const index = row * targetColumns + column;
          const tone = luminance[index];
          const left = column > 0 ? luminance[index - 1] : tone;
          const above = row > 0 ? luminance[index - targetColumns] : tone;
          const edge = Math.min(
            255,
            Math.abs(tone - left) + Math.abs(tone - above),
          );
          const contrasted = clamp(
            ((tone + edge * 0.28) / 255 - 0.5) * 1.45 + 0.5,
            0,
            1,
          );
          line += charset[Math.round(contrasted * (charset.length - 1))];
        }
        context.fillText(line, 0, row * cellHeight);
      }

      context.restore();
    };

    const cancelRenderLoop = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      }
      if (videoFrame && video.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(videoFrame);
        videoFrame = 0;
      }
    };

    const hasReachedEnd = () =>
      video.ended &&
      Number.isFinite(video.duration) &&
      video.duration > 0 &&
      video.currentTime >= video.duration - completionThreshold;

    const revealDiscount = () => {
      if (completed || disposed) return;
      completed = true;
      cancelRenderLoop();
      video.pause();
      setInteractionRequired(false);
      root.classList.add("is-ended");
      canvas.hidden = true;
      discount.hidden = false;
      document.body.classList.add("frml-ascii-ended");
    };

    const checkForCompletion = () => {
      if (hasReachedEnd()) {
        revealDiscount();
        return true;
      }
      return false;
    };

    const scheduleRender = () => {
      if (
        disposed ||
        completed ||
        document.hidden ||
        video.paused ||
        video.ended ||
        animationFrame ||
        videoFrame
      ) {
        return;
      }

      const frameInterval =
        canvas.clientWidth < 640 ? mobileFrameInterval : desktopFrameInterval;

      if (video.requestVideoFrameCallback) {
        videoFrame = video.requestVideoFrameCallback((now) => {
          videoFrame = 0;
          if (disposed || completed || document.hidden || video.paused) return;
          if (now - lastFrameAt >= frameInterval) {
            render();
            lastFrameAt = now;
          }
          scheduleRender();
        });
        return;
      }

      animationFrame = requestAnimationFrame((now) => {
        animationFrame = 0;
        if (disposed || completed || document.hidden || video.paused) return;
        if (now - lastFrameAt >= frameInterval) {
          render();
          lastFrameAt = now;
        }
        scheduleRender();
      });
    };

    const attemptPlay = async (showFallback = true) => {
      if (disposed || completed || checkForCompletion()) return;

      resize();
      render();
      video.muted = true;

      try {
        await video.play();
        if (disposed || completed) return;
        setInteractionRequired(false);
        lastMediaTime = video.currentTime;
        lastProgressAt = performance.now();
        scheduleRender();
      } catch {
        cancelRenderLoop();
        if (showFallback && !disposed && !completed) {
          setInteractionRequired(true);
        }
      }
    };

    const recoverPlayback = () => {
      if (disposed || completed || checkForCompletion()) return;
      automaticRecoveryAttempts = 0;
      setInteractionRequired(false);

      if (!video.error) {
        void attemptPlay(true);
        return;
      }

      const resumeAt = video.currentTime;
      const restorePosition = () => {
        if (
          Number.isFinite(resumeAt) &&
          resumeAt > 0 &&
          Number.isFinite(video.duration)
        ) {
          video.currentTime = Math.min(
            resumeAt,
            Math.max(0, video.duration - completionThreshold * 2),
          );
        }
        void attemptPlay(true);
      };
      video.addEventListener("loadedmetadata", restorePosition, { once: true });
      video.load();
    };

    const handleVisibility = () => {
      if (document.hidden) {
        video.pause();
        cancelRenderLoop();
      } else {
        void attemptPlay(true);
      }
    };
    const handlePageShow = () => {
      if (!checkForCompletion()) void attemptPlay(true);
    };
    const handleInteraction = () => {
      if (!completed && video.paused) void attemptPlay(false);
    };
    const handleEnded = () => {
      if (hasReachedEnd()) {
        revealDiscount();
        return;
      }

      cancelRenderLoop();
      if (prematureEndRecoveries < 2) {
        prematureEndRecoveries += 1;
        video.currentTime = Math.max(0, lastMediaTime - 0.25);
        void attemptPlay(true);
      } else {
        setInteractionRequired(true);
      }
    };
    const handleTimeUpdate = () => {
      if (checkForCompletion()) return;
      const now = performance.now();
      if (video.currentTime > lastMediaTime + 0.05) {
        lastMediaTime = video.currentTime;
        lastProgressAt = now;
        automaticRecoveryAttempts = 0;
      }
    };
    const handlePlaybackError = () => {
      cancelRenderLoop();
      if (!completed) setInteractionRequired(true);
    };
    const handlePause = () => {
      if (disposed || completed || document.hidden || hasReachedEnd()) return;
      clearTimeout(pauseRecoveryTimer);
      pauseRecoveryTimer = window.setTimeout(() => {
        if (!disposed && !completed && !document.hidden && video.paused) {
          void attemptPlay(true);
        }
      }, 600);
    };
    const handleResize = () => {
      resize();
      render();
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(canvas);
    const stallWatchdog = window.setInterval(() => {
      if (
        disposed ||
        completed ||
        document.hidden ||
        video.paused ||
        checkForCompletion()
      ) {
        return;
      }

      const now = performance.now();
      if (video.currentTime > lastMediaTime + 0.05) {
        lastMediaTime = video.currentTime;
        lastProgressAt = now;
        automaticRecoveryAttempts = 0;
        return;
      }
      if (now - lastProgressAt < stallThreshold) return;

      cancelRenderLoop();
      if (automaticRecoveryAttempts < 1) {
        automaticRecoveryAttempts += 1;
        lastProgressAt = now;
        video.pause();
        void attemptPlay(true);
      } else {
        setInteractionRequired(true);
      }
    }, 2000);

    video.addEventListener("loadeddata", handlePageShow);
    video.addEventListener("canplay", handlePageShow);
    video.addEventListener("playing", scheduleRender);
    video.addEventListener("loadedmetadata", checkForCompletion);
    video.addEventListener("durationchange", checkForCompletion);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("pause", handlePause);
    video.addEventListener("error", handlePlaybackError);
    resumeButton.addEventListener("click", recoverPlayback);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("resize", handleResize, { passive: true });
    window.addEventListener("pointerdown", handleInteraction, { passive: true });
    window.addEventListener("touchstart", handleInteraction, { passive: true });

    requestAnimationFrame(() => void attemptPlay(true));

    const cleanup = () => {
      disposed = true;
      video.pause();
      cancelRenderLoop();
      clearInterval(stallWatchdog);
      clearTimeout(pauseRecoveryTimer);
      resizeObserver.disconnect();
      document.body.classList.remove("frml-ascii-ended");
      video.removeEventListener("loadeddata", handlePageShow);
      video.removeEventListener("canplay", handlePageShow);
      video.removeEventListener("playing", scheduleRender);
      video.removeEventListener("loadedmetadata", checkForCompletion);
      video.removeEventListener("durationchange", checkForCompletion);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("error", handlePlaybackError);
      resumeButton.removeEventListener("click", recoverPlayback);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointerdown", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      cleanups.delete(root);
    };
    cleanups.set(root, cleanup);
  }

  function mountAll(scope = document) {
    scope.querySelectorAll("[data-frml-ascii]").forEach(mount);
  }

  mountAll();
  document.addEventListener("shopify:section:load", (event) =>
    mountAll(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-frml-ascii]").forEach((root) => {
      cleanups.get(root)?.();
    });
  });
})();
